import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, pct, dateIt } from "@/lib/format";
import { PageHeader, DecisionBadge, QuoteStatusBadge, PrivacyBadge, Field, ProposalStatusBadge, ProposalProgress, OperationStatusBadge } from "@/components/shared";
import PropertyForm, { buildPayload, propertyFromRecord, estimatedProfit, sheetCompletion } from "@/components/PropertyForm";
import AuthImage from "@/components/AuthImage";
import PhotoLightbox from "@/components/PhotoLightbox";
import { DOC_CHECKLIST, PROPERTY_STATUSES, PROPERTY_STATUS_CLS, CONTACT_ROLE_CLS, OPERATION_TYPES } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowLeft, Calculator, ClipboardList, User, FileText, CalendarClock, History, XCircle, RotateCcw, Save, Handshake, Trash2, Pencil, Image as ImageIcon, Video, FileDown, Eye, EyeOff, Workflow } from "lucide-react";

function SectionTitle({ children }) {
  return <div className="col-span-full mt-5 mb-1 first:mt-0 border-b border-border pb-1 text-sm font-bold uppercase tracking-wide text-primary">{children}</div>;
}

function Row({ label, value, empty, mono, full }) {
  const isEmpty = empty !== undefined ? empty : (value === "" || value === null || value === undefined);
  return (
    <div className={full ? "col-span-full" : ""}>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
      {isEmpty
        ? <span className="mt-1 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid="field-todo">Da completare</span>
        : <div className={`text-sm text-slate-800 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>}
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [p, setP] = useState(null);
  const [statusVal, setStatusVal] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [activating, setActivating] = useState(false);

  const reload = useCallback(() => {
    api.get(`/properties/${id}`).then((r) => { setP(r.data); setStatusVal(r.data.status); }).catch(() => setP(false));
  }, [id]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)); if (isAdmin) api.get("/auth/users").then((r) => setUsers(r.data)); }, [isAdmin]);

  const openEdit = () => { setForm(propertyFromRecord(p)); setEditOpen(true); };
  const saveEdit = async () => {
    if (!form.address.trim()) { toast.error("L'indirizzo è obbligatorio"); return; }
    setSaving(true);
    try {
      await api.put(`/properties/${id}`, buildPayload(form));
      toast.success("Scheda tecnica aggiornata");
      setEditOpen(false); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const updateStatus = async () => {
    try { await api.post(`/properties/${id}/status`, { status: statusVal }); toast.success("Stato aggiornato"); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const doDiscard = async () => {
    try { await api.post(`/properties/${id}/discard`, { reason }); toast.success("Opportunità scartata"); setDiscardOpen(false); setReason(""); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const reactivate = async () => {
    try { await api.post(`/properties/${id}/reactivate`); toast.success("Opportunità riattivata"); reload(); }    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const doActivate = async (type) => {
    setActivating(true);
    try {
      await api.post(`/properties/${id}/activate-operation`, { type });
      toast.success(`Operazione attivata: ${type}`);
      setActivateOpen(false);
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setActivating(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Immobile eliminato definitivamente");
      navigate("/immobili");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setDeleting(false); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get(`/properties/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `scheda_${(p.address || "immobile").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Esportazione PDF non riuscita"); }
  };

  if (p === null) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-xl" /></div>;
  if (p === false) return <div className="py-16 text-center text-muted-foreground">Immobile non trovato.</div>;

  const docs = DOC_CHECKLIST.map((n) => (p.documents || []).find((d) => d.name === n) || { name: n, done: false });
  const lastEval = p.evaluations?.[0];
  const photos = (p.photos || []).filter(Boolean);
  const videos = (p.videos || []).filter(Boolean);
  const hasContacts = (p.owner || "").trim() || (p.contacts || []).length > 0;
  const profit = estimatedProfit(p);
  const completion = sheetCompletion(p);
  const activeOp = (p.operations || [])[0] || null;

  return (
    <div data-testid="property-detail">
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => navigate("/immobili")}><ArrowLeft className="h-4 w-4 mr-1" />Immobili</Button>
      <div className="flex items-start gap-4 mb-2">
        <div className="h-16 w-24 shrink-0 rounded-lg overflow-hidden border border-border bg-muted grid place-items-center cursor-pointer hover:opacity-90 transition-opacity" data-testid="detail-cover" onClick={() => photos.length && setLightbox(0)}>
          {photos[0]
            ? <AuthImage src={photos[0]} className="h-full w-full object-cover" />
            : <ImageIcon className="h-5 w-5 text-slate-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <PageHeader title={p.address} subtitle={`${p.property_type} · ${p.city}${p.province ? ` (${p.province})` : ""}`}>
            <Button variant="outline" onClick={openEdit} data-testid="edit-sheet-btn"><Pencil className="h-4 w-4 mr-2" />Modifica scheda</Button>
            <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-btn"><FileDown className="h-4 w-4 mr-2" />Esporta PDF</Button>
            <Button onClick={() => navigate(`/valutazione?property=${p.id}`)} data-testid="evaluate-property-btn"><Calculator className="h-4 w-4 mr-2" />Valuta Acquisizione</Button>
            <Button variant="outline" className="text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setDelOpen(true)} data-testid="delete-property-btn"><Trash2 className="h-4 w-4 mr-2" />Elimina</Button>
          </PageHeader>
        </div>
      </div>

      <Card className="p-4 mb-6" data-testid="completion-card">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Completamento scheda tecnica</span>
          <span className={`text-sm font-bold ${completion === 100 ? "text-emerald-600" : completion >= 60 ? "text-amber-600" : "text-rose-600"}`} data-testid="completion-pct">{completion}%</span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full transition-all ${completion === 100 ? "bg-emerald-500" : completion >= 60 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${completion}%` }} data-testid="completion-bar" />
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* SCHEDA TECNICA */}
          <Card className="p-6" data-testid="tech-sheet">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <ClipboardList className="h-4 w-4 text-primary shrink-0" />
                <h3 className="font-semibold text-slate-800 truncate">Scheda tecnica immobile</h3>
                {activeOp && (
                  <span className="ml-1 hidden sm:flex items-center gap-1.5" data-testid="operation-status-inline">
                    <OperationStatusBadge status={activeOp.status} />
                    <span className="text-xs text-muted-foreground">{activeOp.type}</span>
                  </span>
                )}
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-primary shrink-0" onClick={() => setSheetOpen((o) => !o)} data-testid="sheet-eye-btn" aria-label={sheetOpen ? "Chiudi scheda" : "Apri scheda"} title={sheetOpen ? "Chiudi scheda" : "Apri scheda"}>
                {sheetOpen ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {!sheetOpen && <p className="text-xs text-muted-foreground mt-1">Clicca sull'occhio per visualizzare la scheda tecnica completa.</p>}
            {sheetOpen && (
            <>
            <div className="flex flex-wrap items-center gap-2 mt-3 mb-3">
              <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-sheet-btn-inline"><Pencil className="h-3.5 w-3.5 mr-1.5" />Modifica scheda</Button>
              {activeOp ? (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5" data-testid="operation-active-box">
                  <Workflow className="h-3.5 w-3.5 text-primary" />
                  <span className="text-sm text-slate-700">Operazione attiva:</span>
                  <OperationStatusBadge status={activeOp.status} />
                  <span className="text-xs text-muted-foreground">{activeOp.type}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-primary" onClick={() => navigate(`/operazioni/${activeOp.id}`)} data-testid="open-operation-btn">Apri operazione</Button>
                </div>
              ) : (
                <Button size="sm" onClick={() => setActivateOpen(true)} data-testid="activate-op-inline"><Workflow className="h-3.5 w-3.5 mr-1.5" />Attiva operazione</Button>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-x-4 gap-y-3">
              <SectionTitle>Dati identificativi</SectionTitle>
              <Row label="Indirizzo" value={p.address} full />
              <Row label="Comune" value={p.city} />
              <Row label="Provincia" value={p.province} />
              <Row label="CAP" value={p.cap} />
              <Row label="Tipologia" value={p.property_type} />
              <Row label="Stato immobile" value={p.status} />
              <Row label="Proprietario / contatti" value={hasContacts ? (p.owner || (p.contacts || []).map((c) => c.name).join(", ")) : ""} empty={!hasContacts} />
              <Row label="Dati catastali" value={p.cadastral_info} />
              <Row label="Responsabile" value={p.responsible_name} />

              <SectionTitle>Caratteristiche</SectionTitle>
              <Row label="Mq commerciali" value={`${p.sqm_commercial} m²`} empty={!p.sqm_commercial} mono />
              <Row label="Mq calpestabili" value={`${p.sqm_walkable} m²`} empty={!p.sqm_walkable} mono />
              <Row label="Locali" value={p.rooms} empty={!p.rooms} mono />
              <Row label="Camere" value={p.bedrooms} empty={!p.bedrooms} mono />
              <Row label="Bagni" value={p.bathrooms} empty={!p.bathrooms} mono />
              <Row label="Piano" value={p.floor} />
              <Row label="Totale piani" value={p.total_floors} empty={!p.total_floors} mono />
              <Row label="Ascensore" value={p.elevator ? "Sì" : "No"} empty={false} />
              <Row label="Anno di costruzione" value={p.year_built} />
              <Row label="Stato di conservazione" value={p.condition} />
              <Row label="Esposizione" value={p.exposure} />
              <Row label="Riscaldamento" value={p.heating} />
              <Row label="Classe energetica" value={p.energy_class} />

              <SectionTitle>Balconi, spazi esterni e pertinenze</SectionTitle>
              <Row label="Balconi" value={`${p.balconies}${p.balconies_sqm ? ` · ${p.balconies_sqm} m²` : ""}`} empty={!p.balconies && !p.balconies_sqm} />
              <Row label="Terrazzi" value={`${p.terraces}${p.terraces_sqm ? ` · ${p.terraces_sqm} m²` : ""}`} empty={!p.terraces && !p.terraces_sqm} />
              <Row label="Giardino" value={p.garden_type} />
              <Row label="Garage" value={p.garage ? `Sì${p.garage_sqm ? ` · ${p.garage_sqm} m²` : ""}` : "No"} empty={false} />
              <Row label="Posto auto" value={p.parking_spot ? "Sì" : "No"} empty={false} />
              <Row label="Cantina" value={p.cellar ? "Sì" : "No"} empty={false} />
              <Row label="Soffitta / Mansarda" value={p.attic ? "Sì" : "No"} empty={false} />
              <Row label="Dépendance" value={p.dependance} />
              <Row label="Terreno" value={p.land} />
              <Row label="Altre pertinenze" value={p.other_appurtenances} full />

              <SectionTitle>Dati economici</SectionTitle>
              <Row label="Prezzo richiesto" value={eur(p.asking_price)} empty={!p.asking_price} mono />
              <Row label="Prezzo proposto" value={eur(p.proposed_price)} empty={!p.proposed_price} mono />
              <Row label="Costo di acquisto" value={eur(p.purchase_cost)} empty={!p.purchase_cost} mono />
              <Row label="Costo ristrutturazione" value={eur(p.renovation_cost)} empty={!p.renovation_cost} mono />
              <Row label="Valore stimato rivendita" value={eur(p.estimated_resale_value)} empty={!p.estimated_resale_value} mono />
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Utile stimato</div>
                <div className={`text-sm mt-0.5 font-mono font-semibold ${profit >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="detail-estimated-profit">{eur(profit)}</div>
              </div>

              <SectionTitle>Documentazione e contenuti</SectionTitle>
              <div className="col-span-full">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Documentazione</div>
                <div className="flex flex-wrap gap-2">
                  {docs.map((d) => (
                    <span key={d.name} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${d.done ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                      {d.done ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}{d.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="col-span-full">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" />Fotografie ({photos.length})
                  {photos.length > 0 && <button onClick={() => setLightbox(0)} className="ml-auto text-primary font-semibold normal-case hover:underline" data-testid="open-gallery">Apri galleria</button>}
                </div>
                {photos.length === 0 ? <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Da completare</span> : (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {photos.map((src, i) => (
                      <button type="button" key={i} className="text-left cursor-pointer group" data-testid={`photo-${i}`} onClick={() => setLightbox(i)}>
                        <div className="aspect-square overflow-hidden rounded-lg border border-border bg-muted group-hover:opacity-90 transition-opacity">
                          <AuthImage src={src} className="h-full w-full object-cover" />
                        </div>
                        {(p.photo_captions || {})[src] && <div className="mt-1 text-xs text-slate-600 truncate" data-testid={`photo-caption-view-${i}`}>{(p.photo_captions || {})[src]}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-full">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5"><Video className="h-3.5 w-3.5" />Video ({videos.length})</div>
                {videos.length === 0 ? <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Da completare</span> : (
                  <ul className="space-y-1">
                    {videos.map((src, i) => <li key={i}><a href={src} target="_blank" rel="noreferrer" className="text-sm text-primary underline break-all" data-testid={`video-${i}`}>{src}</a></li>)}
                  </ul>
                )}
              </div>
              <Row label="Note tecniche" value={p.technical_notes} full />
              <Row label="Note legali" value={p.legal_notes} full />
              <Row label="Note interne" value={p.notes} full />
            </div>
            </>
            )}
          </Card>

          <Card className="p-6" data-testid="status-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2"><History className="h-4 w-4 text-primary" />Gestione Stato</h3>
              <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PROPERTY_STATUS_CLS[p.status] || ""}`} data-testid="current-status">{p.status}</span>
            </div>
            {p.status === "Scartata" ? (
              <div className="space-y-3">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm"><span className="text-muted-foreground">Motivo: </span>{p.discard_reason || "—"}</div>
                <Button onClick={reactivate} data-testid="reactivate-btn"><RotateCcw className="h-4 w-4 mr-2" />Riattiva</Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1">
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Stato pipeline</label>
                  <Select value={statusVal} onValueChange={setStatusVal}>
                    <SelectTrigger className="mt-1.5" data-testid="status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROPERTY_STATUSES.filter((s) => s !== "Scartata").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={updateStatus} data-testid="update-status-btn"><Save className="h-4 w-4 mr-2" />Aggiorna Stato</Button>
                <Button variant="outline" className="text-rose-600 hover:text-rose-700" onClick={() => setDiscardOpen(true)} data-testid="discard-btn"><XCircle className="h-4 w-4 mr-2" />Segna come scartata</Button>
              </div>
            )}
            {p.status === "Incassata / Conclusa" && (
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                <Row label="Data chiusura" value={dateIt(p.closing_date)} />
                <Row label="Importo incassato" value={eur(p.amount_received)} empty={!p.amount_received} mono />
                <Row label="Utile operazione" value={eur((p.amount_received || 0) - (p.total_costs || 0))} empty={false} mono />
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Storico Avanzamenti</div>
              <ol className="space-y-1.5">
                {(p.status_history || []).slice().reverse().map((h, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    <span className="font-medium text-slate-700">{h.status}</span>
                    <span className="text-xs text-muted-foreground">· {dateIt(h.at)} · {h.by_name}</span>
                  </li>
                ))}
                {(!p.status_history || p.status_history.length === 0) && <li className="text-sm text-muted-foreground">Nessuno storico.</li>}
              </ol>
            </div>
          </Card>

          {lastEval && (
            <Card className={`p-6 border-l-4 ${lastEval.decision === "BUY" ? "border-l-emerald-600" : lastEval.decision === "NEGOTIATE" ? "border-l-amber-500" : "border-l-rose-600"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" />Ultima Valutazione</h3>
                <DecisionBadge decision={lastEval.decision} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Row label="Costo totale" value={eur(lastEval.base?.total_cost)} empty={false} mono />
                <Row label="Utile stimato" value={eur(lastEval.base?.profit)} empty={false} mono />
                <Row label="ROI" value={pct(lastEval.base?.roi)} empty={false} mono />
              </div>
              <div className="text-xs text-muted-foreground mt-3">Valutato il {dateIt(lastEval.created_at)} da {lastEval.created_by_name}</div>
            </Card>
          )}

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Handshake className="h-4 w-4 text-primary" />Proposte immobiliari ({p.proposals?.length || 0})</h3>
              <Button size="sm" variant="outline" onClick={() => navigate(`/proposte?property=${p.id}`)} data-testid="new-proposal-from-property"><Handshake className="h-4 w-4 mr-2" />Nuova proposta</Button>
            </div>
            <div className="space-y-3">
              {(p.proposals || []).length === 0 && <div className="text-sm text-muted-foreground">Nessuna proposta registrata.</div>}
              {(p.proposals || []).map((pr) => (
                <div key={pr.id} className="rounded-lg border border-border px-3 py-3" data-testid={`detail-proposal-${pr.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-sm">
                      <span className="font-medium text-slate-800">{eur(pr.proposed_price)}</span>
                      {pr.contact_name && <span className="text-muted-foreground"> · {pr.contact_name}</span>}
                    </div>
                    <ProposalStatusBadge status={pr.status} />
                  </div>
                  <ProposalProgress status={pr.status} />
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
                    <div><span className="font-semibold text-slate-600">Proposta:</span> {dateIt(pr.proposal_date) || "—"}</div>
                    <div><span className="font-semibold text-slate-600">Scadenza:</span> {dateIt(pr.expiry_date) || "—"}</div>
                    <div><span className="font-semibold text-slate-600">Risposta:</span> {dateIt(pr.response_date) || "—"}</div>
                    <div><span className="font-semibold text-slate-600">Azione:</span> {pr.next_action || "—"}</div>
                  </div>
                  {pr.notes && <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">{pr.notes}</div>}
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Preventivi Collegati ({p.quotes?.length || 0})</h3>
            <div className="space-y-2">
              {(p.quotes || []).length === 0 && <div className="text-sm text-muted-foreground">Nessun preventivo collegato.</div>}
              {(p.quotes || []).map((q) => (
                <div key={q.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div><div className="text-sm font-medium">{q.number}</div><div className="text-xs text-muted-foreground">{q.recipient} · {eur(q.total)}</div></div>
                  <QuoteStatusBadge status={q.status} />
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />Follow-up Collegati ({p.followups?.length || 0})</h3>
            <div className="space-y-2">
              {(p.followups || []).length === 0 && <div className="text-sm text-muted-foreground">Nessun follow-up collegato.</div>}
              {(p.followups || []).map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div><div className="text-sm font-medium">{f.type} — {f.next_action}</div><div className="text-xs text-muted-foreground">{f.responsible_name}</div></div>
                  <div className="text-xs text-muted-foreground">{dateIt(f.next_action_date)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Checklist Documenti</h3>
            <div className="space-y-2">
              {docs.map((d) => (
                <div key={d.name} className="flex items-center gap-2.5 text-sm">
                  {d.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
                  <span className={d.done ? "text-slate-800" : "text-muted-foreground"}>{d.name}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><User className="h-4 w-4 text-primary" />Contatti Collegati</h3>
            <div className="space-y-2">
              {(p.contacts || []).length === 0 && <div className="text-sm text-muted-foreground">Nessun contatto collegato.</div>}
              {(p.contacts || []).map((c) => (
                <div key={c.id} className="rounded-lg border border-border px-3 py-2" data-testid={`detail-linked-contact-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-800">{c.name}</div>
                    <PrivacyBadge status={c.privacy_status} />
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${CONTACT_ROLE_CLS[c.role] || CONTACT_ROLE_CLS["Referente"]}`}>{c.role || "Proprietario"}</span>
                    {c.company && <span className="text-xs text-muted-foreground truncate">{c.company}</span>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <PhotoLightbox photos={photos} captions={p.photo_captions || {}} index={lightbox} onIndexChange={setLightbox} onClose={() => setLightbox(null)} />

      {/* Edit technical sheet */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Scheda tecnica immobile</DialogTitle>
            <DialogDescription>Aggiorna la scheda tecnica in tutte le sezioni.</DialogDescription>
          </DialogHeader>
          {form && <PropertyForm form={form} setForm={setForm} isAdmin={isAdmin} users={users} contacts={contacts} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-sheet-btn">{saving ? "Salvataggio..." : "Salva scheda"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={activateOpen} onOpenChange={(o) => !o && setActivateOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Attiva operazione</DialogTitle>
            <DialogDescription>Scegli la tipologia di operazione per <strong>{p.address}</strong>. La scheda tecnica deve essere compilata nei dati principali (indirizzo, comune, tipologia, proprietario/contatto e almeno un prezzo).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            {OPERATION_TYPES.map((t) => (
              <Button key={t} variant="outline" className="justify-start h-auto py-3" disabled={activating} onClick={() => doActivate(t)} data-testid={`activate-type-${t}`}>{t}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Segna come scartata</DialogTitle></DialogHeader>
          <Field label="Motivo (opzionale)"><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="discard-reason" placeholder="Es. prezzo troppo alto, problemi legali..." /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardOpen(false)}>Annulla</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doDiscard} data-testid="confirm-discard">Scarta opportunità</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina definitivamente</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicura di voler eliminare definitivamente questo immobile? Tutti i dati collegati verranno rimossi e l'operazione non potrà essere recuperata.
              <span className="block mt-2 font-medium text-slate-700">{p.address}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-property">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doDelete} disabled={deleting} data-testid="confirm-delete-property">
              {deleting ? "Eliminazione..." : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
